'use client';

import { c as _c } from "react/compiler-runtime";
import { useWallets } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { type FormEvent, type ReactNode, useEffect, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { useAnnouncer } from '@/components/ui/announcer';
import { Icon } from '@/components/ui/icon';
import { Loading03Icon } from '@/components/ui/icon/glyphs';
import { ModalFooter } from '@/components/ui/modal-footer';
import { MotionAnimatedAutoHeight } from '@/components/ui/motion-animated-auto-height';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { StepIndicator } from '@/components/ui/step-indicator';
import { MoveFundsConfirmStep } from '@/components/wallet/move-funds-confirm-step';
import { MoveFundsInputStep } from '@/components/wallet/move-funds-input-step';
import { walletDisplayLabel } from '@/components/wallet/wallet-display-label';
import { useWalletPicker } from '@/components/wallet/wallet-picker-dropdown';
import { D6_SCALE, USDC_DUST_THRESHOLD } from '@/config/solana';
import { type TransferStep, useTransferUsdc } from '@/hooks/wallet/use-transfer-usdc';
import { useFormattedCurrency } from '@/lib/currency';
import { getActiveNetwork } from '@/lib/network/active';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';
import { useWalletBalances } from '@/queries/wallet-balance';

// ---------------------------------------------------------------------------
// Helpers (shared with transfer-funds)
// ---------------------------------------------------------------------------

type DialogStep = 'input' | 'confirm' | 'processing' | 'result';
function truncateAddress(address: string): string {
  if (address.length <= 12) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}
function getExplorerUrl(signature: string): string {
  const network = getActiveNetwork();
  if (network === 'mainnet') {
    return `https://explorer.solana.com/tx/${signature}`;
  }
  return `https://explorer.solana.com/tx/${signature}?cluster=${network}`;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface MoveFundsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MoveFunds(t0) {
  const $ = _c(158);
  const {
    open,
    onOpenChange
  } = t0;
  const t = useTranslations("wallet.move");
  const {
    announce
  } = useAnnouncer();
  const {
    wallets
  } = useWallets();
  const {
    formatBalance
  } = useFormattedCurrency();
  const {
    selectedWalletId,
    setSelectedWalletId,
    selectedWallet
  } = useWalletPicker(open);
  const {
    step: txStep,
    error: txError,
    signature,
    submit,
    reset
  } = useTransferUsdc();
  const {
    enabled: multiWalletsEnabled,
    canStartOperation
  } = useMultiWalletFeature();
  const [dialogStep, setDialogStep] = useState("input");
  const [destinationWalletId, setDestinationWalletId] = useState("");
  const [amount, setAmount] = useState("");
  const [inputError, setInputError] = useState(null);
  const selectedAddress = selectedWallet?.address;
  let t1;
  if ($[0] !== selectedAddress) {
    t1 = selectedAddress !== undefined ? [selectedAddress] : [];
    $[0] = selectedAddress;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    perWallet
  } = useWalletBalances(t1);
  const balance = selectedAddress !== undefined ? perWallet.get(selectedAddress) ?? 0 : 0;
  let t2;
  if ($[2] !== wallets) {
    t2 = wallets.filter(_temp);
    $[2] = wallets;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const activeWallets = t2;
  let t3;
  if ($[4] !== destinationWalletId || $[5] !== wallets) {
    let t4;
    if ($[7] !== destinationWalletId) {
      t4 = w => w.id === destinationWalletId;
      $[7] = destinationWalletId;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    t3 = wallets.find(t4);
    $[4] = destinationWalletId;
    $[5] = wallets;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const destinationWallet = t3;
  let t4;
  let t5;
  if ($[9] !== activeWallets || $[10] !== open || $[11] !== selectedWalletId) {
    t4 = () => {
      if (open && selectedWalletId !== "") {
        const firstOther = activeWallets.find(w_0 => w_0.id !== selectedWalletId);
        if (firstOther !== undefined) {
          setDestinationWalletId(firstOther.id);
        }
      }
    };
    t5 = [open, selectedWalletId, activeWallets];
    $[9] = activeWallets;
    $[10] = open;
    $[11] = selectedWalletId;
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[14] !== txStep) {
    t6 = () => {
      if (txStep === "submitting") {
        setDialogStep("processing");
      } else {
        if (txStep === "confirmed" || txStep === "failed") {
          setDialogStep("result");
        }
      }
    };
    t7 = [txStep];
    $[14] = txStep;
    $[15] = t6;
    $[16] = t7;
  } else {
    t6 = $[15];
    t7 = $[16];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[17] !== multiWalletsEnabled || $[18] !== onOpenChange || $[19] !== open || $[20] !== txStep) {
    t8 = () => {
      if (!multiWalletsEnabled && open && txStep !== "submitting") {
        onOpenChange(false);
      }
    };
    t9 = [multiWalletsEnabled, onOpenChange, open, txStep];
    $[17] = multiWalletsEnabled;
    $[18] = onOpenChange;
    $[19] = open;
    $[20] = txStep;
    $[21] = t8;
    $[22] = t9;
  } else {
    t8 = $[21];
    t9 = $[22];
  }
  useEffect(t8, t9);
  let t10;
  if ($[23] !== dialogStep || $[24] !== onOpenChange) {
    t10 = function handleOpenChange(next) {
      if (dialogStep === "processing") {
        return;
      }
      onOpenChange(next);
    };
    $[23] = dialogStep;
    $[24] = onOpenChange;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const handleOpenChange = t10;
  let t11;
  if ($[26] !== reset || $[27] !== setSelectedWalletId) {
    t11 = function resetState() {
      setDialogStep("input");
      setSelectedWalletId("");
      setDestinationWalletId("");
      setAmount("");
      setInputError(null);
      reset();
    };
    $[26] = reset;
    $[27] = setSelectedWalletId;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const resetState = t11;
  const validateAmount = function validateAmount(value) {
    if (value.trim().length === 0) {
      return null;
    }
    const num = Number(value);
    if (Number.isNaN(num) || num <= 0) {
      return t("error_invalid_amount");
    }
    if (num < USDC_DUST_THRESHOLD) {
      return t("error_min_amount", {
        min: String(USDC_DUST_THRESHOLD)
      });
    }
    if (num > balance) {
      return t("error_insufficient_balance");
    }
    return null;
  };
  let t12;
  if ($[29] !== amount || $[30] !== destinationWallet || $[31] !== destinationWalletId || $[32] !== selectedWallet || $[33] !== selectedWalletId || $[34] !== validateAmount) {
    t12 = function isFormValid() {
      if (selectedWallet === undefined) {
        return false;
      }
      if (destinationWallet === undefined) {
        return false;
      }
      if (selectedWalletId === destinationWalletId) {
        return false;
      }
      const trimmedAmount = amount.trim();
      if (trimmedAmount.length === 0) {
        return false;
      }
      if (validateAmount(trimmedAmount) !== null) {
        return false;
      }
      return true;
    };
    $[29] = amount;
    $[30] = destinationWallet;
    $[31] = destinationWalletId;
    $[32] = selectedWallet;
    $[33] = selectedWalletId;
    $[34] = validateAmount;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const isFormValid = t12;
  let t13;
  if ($[36] !== amount || $[37] !== destinationWallet || $[38] !== destinationWalletId || $[39] !== selectedWalletId || $[40] !== t || $[41] !== validateAmount) {
    t13 = async function handleContinue(e) {
      e.preventDefault();
      if (destinationWallet === undefined) {
        setInputError(t("error_no_destination"));
        return;
      }
      if (selectedWalletId === destinationWalletId) {
        setInputError(t("error_same_wallet"));
        return;
      }
      const amtError = validateAmount(amount.trim());
      if (amtError !== null) {
        setInputError(amtError);
        return;
      }
      setDialogStep("confirm");
    };
    $[36] = amount;
    $[37] = destinationWallet;
    $[38] = destinationWalletId;
    $[39] = selectedWalletId;
    $[40] = t;
    $[41] = validateAmount;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  const handleContinue = t13;
  let t14;
  if ($[43] !== amount || $[44] !== canStartOperation || $[45] !== destinationWallet || $[46] !== selectedWallet || $[47] !== submit) {
    t14 = async function handleConfirm() {
      if (selectedWallet === undefined || destinationWallet === undefined) {
        return;
      }
      if (!canStartOperation("move_funds", "wallet_management")) {
        return;
      }
      const amountUsdc = Number(amount);
      const amountD6 = BigInt(Math.round(amountUsdc * D6_SCALE));
      await submit(selectedWallet.id, selectedWallet.address, selectedWallet.type, destinationWallet.address, amountD6);
    };
    $[43] = amount;
    $[44] = canStartOperation;
    $[45] = destinationWallet;
    $[46] = selectedWallet;
    $[47] = submit;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  const handleConfirm = t14;
  let t15;
  if ($[49] !== reset) {
    t15 = function handleTryAgain() {
      reset();
      setDialogStep("input");
    };
    $[49] = reset;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  const handleTryAgain = t15;
  const handleMaxAmount = function handleMaxAmount() {
    if (balance > 0) {
      setAmount(String(balance));
      setInputError(null);
    }
  };
  let t16;
  if ($[51] !== activeWallets || $[52] !== destinationWalletId || $[53] !== setSelectedWalletId) {
    t16 = function handleSourceChange(walletId) {
      setSelectedWalletId(walletId);
      setAmount("");
      setInputError(null);
      if (walletId === destinationWalletId) {
        const firstOther_0 = activeWallets.find(w_1 => w_1.id !== walletId);
        if (firstOther_0 !== undefined) {
          setDestinationWalletId(firstOther_0.id);
        }
      }
    };
    $[51] = activeWallets;
    $[52] = destinationWalletId;
    $[53] = setSelectedWalletId;
    $[54] = t16;
  } else {
    t16 = $[54];
  }
  const handleSourceChange = t16;
  let t17;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = function handleDestinationChange(walletId_0) {
      setDestinationWalletId(walletId_0);
      setInputError(null);
    };
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  const handleDestinationChange = t17;
  let t18;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = function handleAmountChange(value_0) {
      setAmount(value_0);
      setInputError(null);
    };
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  const handleAmountChange = t18;
  let t19;
  let t20;
  if ($[57] !== announce || $[58] !== t || $[59] !== txStep) {
    t19 = () => {
      if (txStep === "submitting") {
        announce(t("sending"));
      }
      if (txStep === "confirmed") {
        announce(t("success_title"));
      }
      if (txStep === "failed") {
        announce(t("failed_title"));
      }
    };
    t20 = [txStep, announce, t];
    $[57] = announce;
    $[58] = t;
    $[59] = txStep;
    $[60] = t19;
    $[61] = t20;
  } else {
    t19 = $[60];
    t20 = $[61];
  }
  useEffect(t19, t20);
  const canMove = activeWallets.length >= 2;
  let t21;
  if ($[62] !== dialogStep || $[63] !== t || $[64] !== txStep) {
    t21 = dialogStep === "confirm" ? t("confirm_title") : dialogStep === "result" ? txStep === "confirmed" ? t("success_title") : t("failed_title") : t("title");
    $[62] = dialogStep;
    $[63] = t;
    $[64] = txStep;
    $[65] = t21;
  } else {
    t21 = $[65];
  }
  const stepTitle = t21;
  const currentStep = dialogStep === "input" ? 1 : dialogStep === "confirm" ? 2 : 3;
  let t22;
  if ($[66] !== activeWallets) {
    t22 = activeWallets.map(_temp2);
    $[66] = activeWallets;
    $[67] = t22;
  } else {
    t22 = $[67];
  }
  const sourceItems = t22;
  let t23;
  if ($[68] !== activeWallets || $[69] !== selectedWalletId) {
    let t24;
    if ($[71] !== selectedWalletId) {
      t24 = w_3 => w_3.id !== selectedWalletId ? [{
        value: w_3.id,
        label: walletDisplayLabel(w_3)
      }] : [];
      $[71] = selectedWalletId;
      $[72] = t24;
    } else {
      t24 = $[72];
    }
    t23 = activeWallets.flatMap(t24);
    $[68] = activeWallets;
    $[69] = selectedWalletId;
    $[70] = t23;
  } else {
    t23 = $[70];
  }
  const destinationItems = t23;
  const T0 = SilkSheet;
  const t24 = open && multiWalletsEnabled;
  const t25 = dialogStep === "processing";
  const t26 = "page";
  const t27 = "sm";
  let t28;
  if ($[73] !== stepTitle) {
    t28 = <span className="text-heading-md">{stepTitle}</span>;
    $[73] = stepTitle;
    $[74] = t28;
  } else {
    t28 = $[74];
  }
  let t29;
  if ($[75] !== currentStep) {
    t29 = <StepIndicator currentStep={currentStep} totalSteps={3} showCaption={false} />;
    $[75] = currentStep;
    $[76] = t29;
  } else {
    t29 = $[76];
  }
  let t30;
  if ($[77] !== t28 || $[78] !== t29) {
    t30 = <div className="flex flex-col gap-3">{t28}{t29}</div>;
    $[77] = t28;
    $[78] = t29;
    $[79] = t30;
  } else {
    t30 = $[79];
  }
  const t31 = isFormValid();
  let t32;
  if ($[80] !== t) {
    t32 = t("continue");
    $[80] = t;
    $[81] = t32;
  } else {
    t32 = $[81];
  }
  let t33;
  if ($[82] !== t) {
    t33 = t("back");
    $[82] = t;
    $[83] = t33;
  } else {
    t33 = $[83];
  }
  let t34;
  if ($[84] !== t) {
    t34 = t("confirm_send");
    $[84] = t;
    $[85] = t34;
  } else {
    t34 = $[85];
  }
  let t35;
  if ($[86] !== t) {
    t35 = t("view_explorer");
    $[86] = t;
    $[87] = t35;
  } else {
    t35 = $[87];
  }
  let t36;
  if ($[88] !== t) {
    t36 = t("try_again");
    $[88] = t;
    $[89] = t36;
  } else {
    t36 = $[89];
  }
  let t37;
  if ($[90] !== t) {
    t37 = t("done");
    $[90] = t;
    $[91] = t37;
  } else {
    t37 = $[91];
  }
  let t38;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = () => setDialogStep("input");
    $[92] = t38;
  } else {
    t38 = $[92];
  }
  let t39;
  if ($[93] !== handleOpenChange) {
    t39 = () => handleOpenChange(false);
    $[93] = handleOpenChange;
    $[94] = t39;
  } else {
    t39 = $[94];
  }
  let t40;
  if ($[95] !== canMove || $[96] !== dialogStep || $[97] !== handleConfirm || $[98] !== handleTryAgain || $[99] !== signature || $[100] !== t31 || $[101] !== t32 || $[102] !== t33 || $[103] !== t34 || $[104] !== t35 || $[105] !== t36 || $[106] !== t37 || $[107] !== t39 || $[108] !== txStep) {
    t40 = <MoveFundsFooter dialogStep={dialogStep} canMove={canMove} formValid={t31} txStep={txStep} signature={signature} continueLabel={t32} backLabel={t33} confirmLabel={t34} explorerLabel={t35} retryLabel={t36} doneLabel={t37} onBack={t38} onConfirm={handleConfirm} onTryAgain={handleTryAgain} onDone={t39} />;
    $[95] = canMove;
    $[96] = dialogStep;
    $[97] = handleConfirm;
    $[98] = handleTryAgain;
    $[99] = signature;
    $[100] = t31;
    $[101] = t32;
    $[102] = t33;
    $[103] = t34;
    $[104] = t35;
    $[105] = t36;
    $[106] = t37;
    $[107] = t39;
    $[108] = txStep;
    $[109] = t40;
  } else {
    t40 = $[109];
  }
  const T1 = MoveFundsContent;
  const t41 = t("available_balance", {
    balance: formatBalance(balance)
  });
  let t42;
  if ($[110] !== selectedWallet) {
    t42 = selectedWallet === undefined ? null : selectedWallet.label ?? truncateAddress(selectedWallet.address);
    $[110] = selectedWallet;
    $[111] = t42;
  } else {
    t42 = $[111];
  }
  let t43;
  if ($[112] !== destinationWallet) {
    t43 = destinationWallet === undefined ? null : destinationWallet.label ?? truncateAddress(destinationWallet.address);
    $[112] = destinationWallet;
    $[113] = t43;
  } else {
    t43 = $[113];
  }
  const t44 = Number(amount);
  let t45;
  if ($[114] !== formatBalance || $[115] !== t44) {
    t45 = formatBalance(t44);
    $[114] = formatBalance;
    $[115] = t44;
    $[116] = t45;
  } else {
    t45 = $[116];
  }
  let t46;
  if ($[117] !== t) {
    t46 = t("sending");
    $[117] = t;
    $[118] = t46;
  } else {
    t46 = $[118];
  }
  let t47;
  if ($[119] !== t) {
    t47 = t("success_message");
    $[119] = t;
    $[120] = t47;
  } else {
    t47 = $[120];
  }
  let t48;
  if ($[121] !== t || $[122] !== txError) {
    t48 = txError ?? t("toast_failed");
    $[121] = t;
    $[122] = txError;
    $[123] = t48;
  } else {
    t48 = $[123];
  }
  let t49;
  if ($[124] !== T1 || $[125] !== amount || $[126] !== canMove || $[127] !== destinationItems || $[128] !== destinationWalletId || $[129] !== dialogStep || $[130] !== handleAmountChange || $[131] !== handleContinue || $[132] !== handleDestinationChange || $[133] !== handleMaxAmount || $[134] !== handleSourceChange || $[135] !== inputError || $[136] !== selectedWalletId || $[137] !== signature || $[138] !== sourceItems || $[139] !== t41 || $[140] !== t42 || $[141] !== t43 || $[142] !== t45 || $[143] !== t46 || $[144] !== t47 || $[145] !== t48 || $[146] !== txStep) {
    t49 = <T1 dialogStep={dialogStep} txStep={txStep} inputError={inputError} canMove={canMove} selectedWalletId={selectedWalletId} sourceItems={sourceItems} onSourceChange={handleSourceChange} destinationWalletId={destinationWalletId} destinationItems={destinationItems} onDestinationChange={handleDestinationChange} amount={amount} onAmountChange={handleAmountChange} availableBalanceLabel={t41} onMaxAmount={handleMaxAmount} onSubmit={handleContinue} confirmFrom={t42} confirmTo={t43} confirmAmount={t45} sendingLabel={t46} successLabel={t47} failureLabel={t48} signature={signature} />;
    $[124] = T1;
    $[125] = amount;
    $[126] = canMove;
    $[127] = destinationItems;
    $[128] = destinationWalletId;
    $[129] = dialogStep;
    $[130] = handleAmountChange;
    $[131] = handleContinue;
    $[132] = handleDestinationChange;
    $[133] = handleMaxAmount;
    $[134] = handleSourceChange;
    $[135] = inputError;
    $[136] = selectedWalletId;
    $[137] = signature;
    $[138] = sourceItems;
    $[139] = t41;
    $[140] = t42;
    $[141] = t43;
    $[142] = t45;
    $[143] = t46;
    $[144] = t47;
    $[145] = t48;
    $[146] = txStep;
    $[147] = t49;
  } else {
    t49 = $[147];
  }
  let t50;
  if ($[148] !== T0 || $[149] !== handleOpenChange || $[150] !== resetState || $[151] !== stepTitle || $[152] !== t24 || $[153] !== t25 || $[154] !== t30 || $[155] !== t40 || $[156] !== t49) {
    t50 = <T0 open={t24} onOpenChange={handleOpenChange} onAfterClose={resetState} guardDismiss={t25} variant={t26} size={t27} title={stepTitle} header={t30} footer={t40}>{t49}</T0>;
    $[148] = T0;
    $[149] = handleOpenChange;
    $[150] = resetState;
    $[151] = stepTitle;
    $[152] = t24;
    $[153] = t25;
    $[154] = t30;
    $[155] = t40;
    $[156] = t49;
    $[157] = t50;
  } else {
    t50 = $[157];
  }
  return t50;
}
function _temp2(w_2) {
  return {
    value: w_2.id,
    label: walletDisplayLabel(w_2)
  };
}
function _temp(wallet) {
  return wallet.archivedAt === null && wallet.isAvailable !== false;
}
interface MoveFundsFooterProps {
  dialogStep: DialogStep;
  canMove: boolean;
  formValid: boolean;
  txStep: TransferStep;
  signature: string | null;
  continueLabel: string;
  backLabel: string;
  confirmLabel: string;
  explorerLabel: string;
  retryLabel: string;
  doneLabel: string;
  onBack: () => void;
  onConfirm: () => Promise<void>;
  onTryAgain: () => void;
  onDone: () => void;
}
function MoveFundsFooter(t0) {
  const $ = _c(31);
  const {
    dialogStep,
    canMove,
    formValid,
    txStep,
    signature,
    continueLabel,
    backLabel,
    confirmLabel,
    explorerLabel,
    retryLabel,
    doneLabel,
    onBack,
    onConfirm,
    onTryAgain,
    onDone
  } = t0;
  if (dialogStep === "processing") {
    return null;
  }
  let t1;
  if ($[0] !== canMove || $[1] !== continueLabel || $[2] !== dialogStep || $[3] !== formValid) {
    t1 = dialogStep === "input" && canMove ? <AnimatedButton type="submit" form="move-funds-form" disabled={!formValid}>{continueLabel}</AnimatedButton> : null;
    $[0] = canMove;
    $[1] = continueLabel;
    $[2] = dialogStep;
    $[3] = formValid;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== backLabel || $[6] !== confirmLabel || $[7] !== dialogStep || $[8] !== onBack || $[9] !== onConfirm) {
    t2 = dialogStep === "confirm" ? <><AnimatedButton variant="secondary" onClick={onBack}>{backLabel}</AnimatedButton><AnimatedButton onClick={onConfirm}>{confirmLabel}</AnimatedButton></> : null;
    $[5] = backLabel;
    $[6] = confirmLabel;
    $[7] = dialogStep;
    $[8] = onBack;
    $[9] = onConfirm;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== dialogStep || $[12] !== explorerLabel || $[13] !== signature || $[14] !== txStep) {
    t3 = dialogStep === "result" && txStep === "confirmed" && signature !== null ? <AnimatedButton variant="secondary" onClick={() => {
      window.open(getExplorerUrl(signature), "_blank");
    }}>{explorerLabel}</AnimatedButton> : null;
    $[11] = dialogStep;
    $[12] = explorerLabel;
    $[13] = signature;
    $[14] = txStep;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== dialogStep || $[17] !== onTryAgain || $[18] !== retryLabel || $[19] !== txStep) {
    t4 = dialogStep === "result" && txStep === "failed" ? <AnimatedButton variant="secondary" onClick={onTryAgain}>{retryLabel}</AnimatedButton> : null;
    $[16] = dialogStep;
    $[17] = onTryAgain;
    $[18] = retryLabel;
    $[19] = txStep;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== dialogStep || $[22] !== doneLabel || $[23] !== onDone) {
    t5 = dialogStep === "result" ? <AnimatedButton onClick={onDone}>{doneLabel}</AnimatedButton> : null;
    $[21] = dialogStep;
    $[22] = doneLabel;
    $[23] = onDone;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  let t6;
  if ($[25] !== t1 || $[26] !== t2 || $[27] !== t3 || $[28] !== t4 || $[29] !== t5) {
    t6 = <ModalFooter>{t1}{t2}{t3}{t4}{t5}</ModalFooter>;
    $[25] = t1;
    $[26] = t2;
    $[27] = t3;
    $[28] = t4;
    $[29] = t5;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  return t6;
}
interface WalletPickerItem {
  value: string;
  label: string;
}
interface MoveFundsContentProps {
  dialogStep: DialogStep;
  txStep: TransferStep;
  inputError: string | null;
  canMove: boolean;
  selectedWalletId: string;
  sourceItems: WalletPickerItem[];
  onSourceChange: (walletId: string) => void;
  destinationWalletId: string;
  destinationItems: WalletPickerItem[];
  onDestinationChange: (walletId: string) => void;
  amount: string;
  onAmountChange: (value: string) => void;
  availableBalanceLabel: string;
  onMaxAmount: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  confirmFrom: string | null;
  confirmTo: string | null;
  confirmAmount: string;
  sendingLabel: string;
  successLabel: string;
  failureLabel: string;
  signature: string | null;
}
function MoveFundsContent(t0) {
  const $ = _c(40);
  const {
    dialogStep,
    txStep,
    inputError,
    canMove,
    selectedWalletId,
    sourceItems,
    onSourceChange,
    destinationWalletId,
    destinationItems,
    onDestinationChange,
    amount,
    onAmountChange,
    availableBalanceLabel,
    onMaxAmount,
    onSubmit,
    confirmFrom,
    confirmTo,
    confirmAmount,
    sendingLabel,
    successLabel,
    failureLabel,
    signature
  } = t0;
  let t1;
  if ($[0] !== canMove || $[1] !== dialogStep || $[2] !== inputError || $[3] !== txStep) {
    t1 = [dialogStep, txStep, inputError, canMove];
    $[0] = canMove;
    $[1] = dialogStep;
    $[2] = inputError;
    $[3] = txStep;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== amount || $[6] !== availableBalanceLabel || $[7] !== canMove || $[8] !== destinationItems || $[9] !== destinationWalletId || $[10] !== dialogStep || $[11] !== inputError || $[12] !== onAmountChange || $[13] !== onDestinationChange || $[14] !== onMaxAmount || $[15] !== onSourceChange || $[16] !== onSubmit || $[17] !== selectedWalletId || $[18] !== sourceItems) {
    t2 = dialogStep === "input" && (!canMove ? <p className="py-4 text-body text-foreground-secondary">You need at least two wallets to move funds between them.</p> : <MoveFundsInputStep selectedWalletId={selectedWalletId} sourceItems={sourceItems} onSourceChange={onSourceChange} destinationWalletId={destinationWalletId} destinationItems={destinationItems} onDestinationChange={onDestinationChange} amount={amount} onAmountChange={onAmountChange} inputError={inputError} availableBalanceLabel={availableBalanceLabel} onMaxAmount={onMaxAmount} onSubmit={onSubmit} />);
    $[5] = amount;
    $[6] = availableBalanceLabel;
    $[7] = canMove;
    $[8] = destinationItems;
    $[9] = destinationWalletId;
    $[10] = dialogStep;
    $[11] = inputError;
    $[12] = onAmountChange;
    $[13] = onDestinationChange;
    $[14] = onMaxAmount;
    $[15] = onSourceChange;
    $[16] = onSubmit;
    $[17] = selectedWalletId;
    $[18] = sourceItems;
    $[19] = t2;
  } else {
    t2 = $[19];
  }
  let t3;
  if ($[20] !== confirmAmount || $[21] !== confirmFrom || $[22] !== confirmTo || $[23] !== dialogStep) {
    t3 = dialogStep === "confirm" && confirmFrom !== null && confirmTo !== null ? <MoveFundsConfirmStep fromValue={confirmFrom} toValue={confirmTo} amountValue={confirmAmount} /> : null;
    $[20] = confirmAmount;
    $[21] = confirmFrom;
    $[22] = confirmTo;
    $[23] = dialogStep;
    $[24] = t3;
  } else {
    t3 = $[24];
  }
  let t4;
  if ($[25] !== dialogStep || $[26] !== sendingLabel) {
    t4 = dialogStep === "processing" ? <div className="flex flex-col items-center gap-4 py-8"><Icon icon={Loading03Icon} className="size-10 animate-spin text-foreground-secondary motion-reduce:animate-none" aria-hidden="true" /><p className="text-body text-foreground-secondary">{sendingLabel}</p></div> : null;
    $[25] = dialogStep;
    $[26] = sendingLabel;
    $[27] = t4;
  } else {
    t4 = $[27];
  }
  let t5;
  if ($[28] !== dialogStep || $[29] !== failureLabel || $[30] !== signature || $[31] !== successLabel || $[32] !== txStep) {
    t5 = dialogStep === "result" ? <div className="flex flex-col gap-4">{txStep === "confirmed" && signature !== null ? <p className="text-body text-foreground-secondary">{successLabel}</p> : null}{txStep === "failed" ? <p className="text-body text-feedback-error-content">{failureLabel}</p> : null}</div> : null;
    $[28] = dialogStep;
    $[29] = failureLabel;
    $[30] = signature;
    $[31] = successLabel;
    $[32] = txStep;
    $[33] = t5;
  } else {
    t5 = $[33];
  }
  let t6;
  if ($[34] !== t1 || $[35] !== t2 || $[36] !== t3 || $[37] !== t4 || $[38] !== t5) {
    t6 = <MotionAnimatedAutoHeight layoutDependencies={t1}>{t2}{t3}{t4}{t5}</MotionAnimatedAutoHeight>;
    $[34] = t1;
    $[35] = t2;
    $[36] = t3;
    $[37] = t4;
    $[38] = t5;
    $[39] = t6;
  } else {
    t6 = $[39];
  }
  return t6;
}